import React, { useState, useRef, useEffect } from 'react';
import { Clock, Check } from 'lucide-react';
import { isAndroid } from '../../utils/platform.utils';

interface CustomTimePickerProps {
  value: string; // HH:MM format (24-hour)
  onChange: (value: string) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  label?: string;
}

/**
 * Custom time picker component that uses:
 * - Native <input type="time"> on iOS and desktop (works well)
 * - Custom modal time picker on ANY Android (native Capacitor WebView picker is
 *   broken, and Chrome's clock-dial dialog on the Android PWA is cumbersome:
 *   tapping the hour does nothing until you open the dial, then hour → minute → OK)
 */
export const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  value,
  onChange,
  disabled = false,
  style = {},
  label
}) => {
  // Use native input on iOS/desktop, custom modal on Android (native app AND PWA)
  const useCustomModal = isAndroid();

  // For native time input (iOS/PWA)
  if (!useCustomModal) {
    return (
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          padding: '16px',
          fontSize: '18px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          color: '#111827',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...style
        }}
      />
    );
  }

  // Custom modal for Android
  return <AndroidCustomTimePicker
    value={value}
    onChange={onChange}
    disabled={disabled}
    style={style}
    label={label}
  />;
};

/**
 * Android-specific custom modal time picker
 * Renders a modal with hour/minute wheels instead of native picker
 */
const AndroidCustomTimePicker: React.FC<CustomTimePickerProps> = ({
  value,
  onChange,
  disabled = false,
  style = {},
  label = 'Select Time'
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Parse current value
  const [hours, minutes] = (value || '12:00').split(':').map(Number);
  const [selectedHour, setSelectedHour] = useState(hours || 12);
  const [selectedMinute, setSelectedMinute] = useState(minutes || 0);

  const containerRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // Format display time
  const formatDisplayTime = (h: number, m: number) => {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  // Empty value (optional field, or an open entry with no clock-out yet) shows a
  // placeholder instead of a misleading "12:00 PM".
  const displayTime = value ? formatDisplayTime(hours || 0, minutes || 0) : '--:--';

  // Callers pass visual styles meant for the <input> (border, padding, font).
  // Put layout-only props on the wrapper and the rest on the trigger button so
  // Android doesn't render a double border.
  const { width, minWidth, maxWidth, flex, margin, marginTop, marginBottom, marginLeft, marginRight, ...buttonStyle } = style;
  const wrapperStyle: React.CSSProperties = { position: 'relative', width, minWidth, maxWidth, flex, margin, marginTop, marginBottom, marginLeft, marginRight };

  // Update selected values when value prop changes
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      setSelectedHour(h);
      setSelectedMinute(m);
    }
  }, [value]);

  // Scroll to selected values when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourListRef.current) {
          const h12 = selectedHour % 12 === 0 ? 12 : selectedHour % 12;
          const hourElement = hourListRef.current.querySelector(`[data-hour="${h12}"]`);
          if (hourElement) {
            hourElement.scrollIntoView({ block: 'center', behavior: 'instant' });
          }
        }
        if (minuteListRef.current) {
          const minuteElement = minuteListRef.current.querySelector(`[data-minute="${selectedMinute}"]`);
          if (minuteElement) {
            minuteElement.scrollIntoView({ block: 'center', behavior: 'instant' });
          }
        }
      }, 50);
    }
  }, [isOpen, selectedHour, selectedMinute]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside as any);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleConfirm = () => {
    const newValue = `${selectedHour.toString().padStart(2, '0')}:${selectedMinute.toString().padStart(2, '0')}`;
    onChange(newValue);
    setIsOpen(false);
  };

  // 12-hour list (12, 1 … 11) + AM/PM toggle instead of scrolling 24 rows, and
  // 5-minute steps instead of 60 rows. An off-step minute already on the entry
  // (e.g. :07 from a raw GPS clock-in) stays selectable so it isn't silently lost.
  const isPM = selectedHour >= 12;
  const hour12 = selectedHour % 12 === 0 ? 12 : selectedHour % 12;
  const hours12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const setHour12 = (h12: number) => setSelectedHour((h12 % 12) + (isPM ? 12 : 0));
  const setPM = (pm: boolean) => setSelectedHour((selectedHour % 12) + (pm ? 12 : 0));
  const minuteOptions = Array.from(
    new Set([...Array.from({ length: 12 }, (_, i) => i * 5), selectedMinute])
  ).sort((a, b) => a - b);

  return (
    <div ref={containerRef} style={wrapperStyle}>
      {/* Time picker trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          padding: '16px',
          fontSize: '18px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          color: value ? '#111827' : '#9ca3af',
          ...buttonStyle,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          outline: 'none'
        }}
      >
        <span style={{
          flex: 1,
          fontWeight: '600',
          fontSize: '20px'
        }}>
          {displayTime}
        </span>
        <Clock
          size={24}
          color="#6b7280"
          style={{ flexShrink: 0 }}
        />
      </button>

      {/* Time picker modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9998
            }}
            onClick={() => setIsOpen(false)}
          />

          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              left: '16px',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'white',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
              zIndex: 9999,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              textAlign: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#374151'
              }}>
                {label}
              </h3>
              <div style={{
                marginTop: '12px',
                fontSize: '32px',
                fontWeight: '700',
                color: '#10b981'
              }}>
                {formatDisplayTime(selectedHour, selectedMinute)}
              </div>
              {/* AM / PM toggle */}
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                {(['AM', 'PM'] as const).map((ap) => {
                  const active = (ap === 'PM') === isPM;
                  return (
                    <button
                      key={ap}
                      type="button"
                      onClick={() => setPM(ap === 'PM')}
                      style={{
                        minWidth: '96px',
                        padding: '12px 16px',
                        fontSize: '18px',
                        fontWeight: '700',
                        borderRadius: '12px',
                        border: active ? '2px solid #10b981' : '2px solid #d1d5db',
                        backgroundColor: active ? '#dcfce7' : 'white',
                        color: active ? '#16a34a' : '#6b7280',
                        cursor: 'pointer'
                      }}
                    >
                      {ap}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time selectors */}
            <div style={{
              display: 'flex',
              padding: '0',
              height: '250px'
            }}>
              {/* Hours column */}
              <div style={{
                flex: 1,
                borderRight: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textAlign: 'center',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  Hour
                </div>
                <div
                  ref={hourListRef}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {hours12.map(h => {
                    const isSelected = h === hour12;
                    const displayHour = `${h} ${isPM ? 'PM' : 'AM'}`;
                    return (
                      <button
                        key={h}
                        type="button"
                        data-hour={h}
                        onClick={() => setHour12(h)}
                        style={{
                          width: '100%',
                          padding: '16px 12px',
                          fontSize: '18px',
                          fontWeight: isSelected ? '700' : '500',
                          backgroundColor: isSelected ? '#dcfce7' : 'white',
                          color: isSelected ? '#16a34a' : '#374151',
                          border: 'none',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {displayHour}
                        {isSelected && <Check size={20} color="#16a34a" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Minutes column */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textAlign: 'center',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  Minute
                </div>
                <div
                  ref={minuteListRef}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {minuteOptions.map(m => {
                    const isSelected = m === selectedMinute;
                    return (
                      <button
                        key={m}
                        type="button"
                        data-minute={m}
                        onClick={() => setSelectedMinute(m)}
                        style={{
                          width: '100%',
                          padding: '16px 12px',
                          fontSize: '18px',
                          fontWeight: isSelected ? '700' : '500',
                          backgroundColor: isSelected ? '#dcfce7' : 'white',
                          color: isSelected ? '#16a34a' : '#374151',
                          border: 'none',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        :{m.toString().padStart(2, '0')}
                        {isSelected && <Check size={20} color="#16a34a" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              display: 'flex',
              gap: '12px'
            }}>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#6b7280',
                  backgroundColor: 'white',
                  border: '2px solid #d1d5db',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                style={{
                  flex: 1,
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'white',
                  backgroundColor: '#10b981',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomTimePicker;
